import addDays from 'date-fns/addDays';
import subDays from 'date-fns/subDays';
import isValid from 'date-fns/isValid';
import isAfter from 'date-fns/isAfter';
import isBefore from 'date-fns/isBefore';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import moment from 'moment';

import {
  DISCOUNT_TEMPLATE_TYPE,
  DISCOUNT_TEMPLATE_PRODUCTS,
} from '../../../constants/discount-templates';
import { DATE_OFFSET_TYPE } from '../../../constants/dates';
import { getCurrencySymbol } from '../../../constants/currency';
import { formatPriceForDisplay } from '../../../util/format-money';

const DATE_FORMAT = 'MMM D';

const PRODUCT_SCOPE_LABELS = {
  [DISCOUNT_TEMPLATE_PRODUCTS.DIGITALS]: 'digital',
  [DISCOUNT_TEMPLATE_PRODUCTS.PRINTS]: 'prints & physical',
  [DISCOUNT_TEMPLATE_PRODUCTS.ALL]: 'all',
};

export const resolveOffsetDate = (
  dateOffset,
  eventStartDate,
  firstPhotoPublishedAt,
) => {
  if (!dateOffset) {
    return null;
  }

  const { offset, offset_type: offsetType } = dateOffset;
  const eventDate = eventStartDate ? new Date(eventStartDate) : null;
  const hasValidEventDate = eventDate && isValid(eventDate);

  if (offsetType === DATE_OFFSET_TYPE.DAY_OF_THE_EVENT) {
    return hasValidEventDate ? eventDate : null;
  }

  if (offset == null || offset < 0) {
    return null;
  }

  switch (offsetType) {
    case DATE_OFFSET_TYPE.DAYS_BEFORE_EVENT:
      return hasValidEventDate ? subDays(eventDate, offset) : null;

    case DATE_OFFSET_TYPE.DAYS_AFTER_EVENT:
    case DATE_OFFSET_TYPE.DAYS_AFTER_EVENT_START:
      return hasValidEventDate ? addDays(eventDate, offset) : null;

    case DATE_OFFSET_TYPE.DAYS_AFTER_FIRST_PHOTO_PUBLISH: {
      if (!firstPhotoPublishedAt) {
        return null;
      }
      const baseDate = new Date(firstPhotoPublishedAt);
      return isValid(baseDate) ? addDays(baseDate, offset) : null;
    }

    default:
      return null;
  }
};

const resolveBoundaryDate = (
  fixedDate,
  dateOffset,
  eventStartDate,
  firstPhotoPublishedAt,
) => {
  if (fixedDate) {
    const parsed = new Date(fixedDate);
    return isValid(parsed) ? parsed : null;
  }

  return resolveOffsetDate(dateOffset, eventStartDate, firstPhotoPublishedAt);
};

const getLeadPhrase = ({ type, discount_value: discountValue }) => {
  switch (type) {
    case DISCOUNT_TEMPLATE_TYPE.FREE_SHIPPING:
      return 'Free shipping';
    case DISCOUNT_TEMPLATE_TYPE.AMOUNT:
      return `Save $${formatPriceForDisplay(discountValue, true)}`;
    case DISCOUNT_TEMPLATE_TYPE.PERCENTAGE:
      return `Save ${discountValue}%`;
    default:
      return null;
  }
};

const getScopePhrase = products => {
  if (!products || !products.length) {
    return null;
  }

  if (products.includes(DISCOUNT_TEMPLATE_PRODUCTS.ALL)) {
    return PRODUCT_SCOPE_LABELS[DISCOUNT_TEMPLATE_PRODUCTS.ALL];
  }

  const labels = products
    .map(product => PRODUCT_SCOPE_LABELS[product])
    .filter(Boolean);

  return labels.length ? labels.join(' and ') : null;
};

const getMinimumClause = minCheckoutAmount => {
  if (!(minCheckoutAmount?.value > 0)) {
    return '';
  }

  const symbol = getCurrencySymbol(minCheckoutAmount.currency) ?? '$';
  return ` when spending over ${symbol}${formatPriceForDisplay(
    minCheckoutAmount.value,
    true,
  )}`;
};

const getTimeWindowClause = endDate => {
  if (!endDate) {
    return '';
  }

  return ` before ${moment(endDate).format(DATE_FORMAT)}`;
};

export const isDiscountActive = (
  startDate,
  endDate,
  currentDate = new Date(),
) => {
  if (startDate && isBefore(currentDate, startOfDay(startDate))) {
    return false;
  }

  if (endDate && isAfter(currentDate, endOfDay(endDate))) {
    return false;
  }

  return true;
};

export const getDiscountBannerText = (
  discountTemplate,
  { eventStartDate, firstPhotoPublishedAt } = {},
) => {
  if (!discountTemplate) {
    return null;
  }

  const lead = getLeadPhrase(discountTemplate);
  const scope = getScopePhrase(discountTemplate.products);

  if (!lead || !scope) {
    return null;
  }

  const startDate = resolveBoundaryDate(
    discountTemplate.start_date,
    discountTemplate.start_date_offset,
    eventStartDate,
    firstPhotoPublishedAt,
  );
  const endDate = resolveBoundaryDate(
    discountTemplate.end_date,
    discountTemplate.end_date_offset,
    eventStartDate,
    firstPhotoPublishedAt,
  );

  if (!isDiscountActive(startDate, endDate)) {
    return null;
  }

  const minimumClause = getMinimumClause(discountTemplate.min_checkout_amount);
  const timeWindowClause = getTimeWindowClause(endDate);

  return `${lead} on ${scope} products${minimumClause}${timeWindowClause}!`;
};

const getAppliedPhrase = ({ type, discount_value: discountValue }) => {
  switch (type) {
    case DISCOUNT_TEMPLATE_TYPE.FREE_SHIPPING:
      return 'Free shipping';
    case DISCOUNT_TEMPLATE_TYPE.AMOUNT:
      return `$${formatPriceForDisplay(discountValue, true)} off`;
    case DISCOUNT_TEMPLATE_TYPE.PERCENTAGE:
      return `${discountValue}% off`;
    default:
      return null;
  }
};

export const getAppliedDiscountLabel = (
  discountTemplate,
  { eventStartDate, firstPhotoPublishedAt } = {},
) => {
  if (!discountTemplate) {
    return null;
  }

  const label = getAppliedPhrase(discountTemplate);

  if (!label) {
    return null;
  }

  const startDate = resolveBoundaryDate(
    discountTemplate.start_date,
    discountTemplate.start_date_offset,
    eventStartDate,
    firstPhotoPublishedAt,
  );
  const endDate = resolveBoundaryDate(
    discountTemplate.end_date,
    discountTemplate.end_date_offset,
    eventStartDate,
    firstPhotoPublishedAt,
  );

  if (!isDiscountActive(startDate, endDate)) {
    return null;
  }

  return label;
};

export const isFreeShippingDiscount = discountTemplate =>
  discountTemplate?.type === DISCOUNT_TEMPLATE_TYPE.FREE_SHIPPING;
